import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { Link } from '#router'
import { Button } from '@/components/ui/button'
import { usePageTitle } from '@/hooks/use-document-title'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { AUDITION_WIZARD_FLAG } from '@/lib/feature-flags'
import Footer from './footer'

const tiers = [
  {
    name: 'Self-Study',
    price: 'Free',
    description:
      'Everything you need to prepare on your own, at your own pace.',
    features: [
      'Full problem platform — write, run, and test solutions',
      '26 technical topics, ~300 curated problems',
      'Structured courses and editorials',
      'Progress tracking',
    ],
    cta: { label: 'Create Your Free Account', href: '/sign-up' },
    highlighted: false,
  },
  {
    name: '1:1 Coaching',
    price: 'Custom',
    description:
      'Sprint-based coaching for engineers who want an experienced interviewer in their corner. Pricing is discussed on your intro call — after we have both decided the program is a fit.',
    features: [
      'Everything in Self-Study',
      '1:1 sessions with an experienced interview coach',
      'Sprint-based structure with honest progress assessments',
      'Mock interviews with actionable feedback',
      'Accountability between sessions',
    ],
    cta: { label: 'Start with a Free Account', href: '/sign-up' },
    highlighted: true,
  },
]

export default function Pricing() {
  usePageTitle('Pricing')
  const { enabled: wizardEnabled } = useFeatureFlag(AUDITION_WIZARD_FLAG)

  const renderedTiers = tiers.map((tier) =>
    tier.highlighted && wizardEnabled
      ? { ...tier, cta: { label: 'Apply Now', href: '/apply' } }
      : tier,
  )

  return (
    <div className="flex min-h-[calc(100vh-4rem)] flex-col">
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          <span className="text-[#51B0BC]">Pricing</span>
        </h1>
        <p className="text-muted-foreground mb-12 max-w-3xl text-lg leading-relaxed">
          Start free. If you want more than self-study, the coaching program
          begins with a conversation — not a checkout page.
        </p>

        <div className="mb-14 grid gap-6 md:grid-cols-2">
          {renderedTiers.map((tier) => (
            <div
              key={tier.name}
              className={`bg-card flex flex-col rounded-xl border p-8 ${
                tier.highlighted ? 'border-[#51B0BC] shadow-lg' : ''
              }`}
            >
              <h2 className="text-xl font-semibold">{tier.name}</h2>
              <p className="text-[#51B0BC] mt-1 text-3xl font-bold">
                {tier.price}
              </p>
              <p className="text-muted-foreground mt-3 leading-relaxed">
                {tier.description}
              </p>
              <ul className="mt-6 mb-8 space-y-3">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <CheckCircle2 className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                    <span className="text-muted-foreground leading-relaxed">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                asChild
                size="lg"
                variant={tier.highlighted ? 'default' : 'outline'}
                className="group mt-auto text-base"
              >
                <Link href={tier.cta.href}>
                  {tier.cta.label}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
            </div>
          ))}
        </div>

        <div className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
          <span>Still deciding?</span>
          <Link
            href="/how-it-works"
            className="text-primary underline underline-offset-4 hover:opacity-80"
          >
            See how the program works
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  )
}
